import { useEffect, useRef, useState } from "react";

import { createFrameTickerWorker } from "./createFrameTickerWorker";
import { drawPipOverlay } from "./pipCanvasUtils";
import type {
  VideoClipPipPosition,
  VideoClipPipShape,
  VideoClipPipSize,
} from "./useVideoClipSettingsStore";

interface UseCompositedRecordingStreamOptions {
  cameraStream: MediaStream | null;
  isMicEnabled: boolean;
  pipPosition: VideoClipPipPosition;
  pipShape: VideoClipPipShape;
  pipSize: VideoClipPipSize;
  screenStream: MediaStream | null;
}

const TARGET_FPS = 30;
const DEFAULT_WIDTH = 1280;
const DEFAULT_HEIGHT = 720;

interface CanvasCaptureVideoTrack extends MediaStreamTrack {
  requestFrame: () => void;
}

const stopOutput = (
  outputStreamRef: React.MutableRefObject<MediaStream | null>,
) => {
  outputStreamRef.current?.getVideoTracks().forEach(track => {
    if (track.readyState === "live") {
      track.stop();
    }
  });
  outputStreamRef.current = null;
};

const hasRequestFrame = (
  track: MediaStreamTrack | undefined,
): track is CanvasCaptureVideoTrack =>
  Boolean(
    track &&
    "requestFrame" in track &&
    typeof track.requestFrame === "function",
  );

const createCanvasCaptureOutput = (
  canvas: HTMLCanvasElement,
): {
  requestFrame: (() => void) | null;
  stream: MediaStream;
} => {
  const manualStream = canvas.captureStream(0);
  const manualTrack = manualStream.getVideoTracks()[0];

  if (hasRequestFrame(manualTrack)) {
    return {
      requestFrame: () => manualTrack.requestFrame(),
      stream: manualStream,
    };
  }

  manualStream.getTracks().forEach(track => {
    if (track.readyState === "live") {
      track.stop();
    }
  });

  return {
    requestFrame: null,
    stream: canvas.captureStream(TARGET_FPS),
  };
};

export const useCompositedRecordingStream = ({
  cameraStream,
  isMicEnabled,
  pipPosition,
  pipShape,
  pipSize,
  screenStream,
}: UseCompositedRecordingStreamOptions): MediaStream | null => {
  const [compositedStream, setCompositedStream] = useState<MediaStream | null>(
    null,
  );
  const outputStreamRef = useRef<MediaStream | null>(null);
  const frameTickerRef = useRef<Worker | null>(null);
  const pipPositionRef = useRef(pipPosition);
  pipPositionRef.current = pipPosition;
  const pipShapeRef = useRef(pipShape);
  pipShapeRef.current = pipShape;
  const pipSizeRef = useRef(pipSize);
  pipSizeRef.current = pipSize;

  useEffect(() => {
    const shouldComposite = Boolean(screenStream && cameraStream);

    if (!shouldComposite || !screenStream || !cameraStream) {
      if (frameTickerRef.current) {
        frameTickerRef.current.terminate();
        frameTickerRef.current = null;
      }
      stopOutput(outputStreamRef);
      setCompositedStream(null);
      return;
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", {
      alpha: false,
      desynchronized: true,
    });
    if (!ctx) {
      setCompositedStream(null);
      return;
    }

    const screenVideo = document.createElement("video");
    screenVideo.muted = true;
    screenVideo.playsInline = true;
    screenVideo.srcObject = screenStream;

    const cameraVideo = document.createElement("video");
    cameraVideo.muted = true;
    cameraVideo.playsInline = true;
    cameraVideo.srcObject = cameraStream;

    let isDisposed = false;

    const cameraVideoTracks = cameraStream.getVideoTracks();

    const drawFrame = () => {
      if (isDisposed) return;

      const width = screenVideo.videoWidth || DEFAULT_WIDTH;
      const height = screenVideo.videoHeight || DEFAULT_HEIGHT;
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }

      if (screenVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        ctx.drawImage(screenVideo, 0, 0, canvas.width, canvas.height);
      } else {
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      const cameraTrack = cameraVideoTracks.find(
        track => track.readyState === "live" && track.enabled,
      );

      if (
        cameraTrack &&
        cameraVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA
      ) {
        drawPipOverlay(
          ctx,
          cameraVideo,
          canvas,
          pipPositionRef.current,
          pipShapeRef.current,
          pipSizeRef.current,
        );
      }
    };

    const start = async () => {
      await Promise.allSettled([screenVideo.play(), cameraVideo.play()]);
      if (isDisposed) return;

      canvas.width = screenVideo.videoWidth || DEFAULT_WIDTH;
      canvas.height = screenVideo.videoHeight || DEFAULT_HEIGHT;

      const { requestFrame, stream } = createCanvasCaptureOutput(canvas);
      outputStreamRef.current = stream;
      setCompositedStream(stream);

      drawFrame();
      requestFrame?.();

      try {
        const frameTicker = createFrameTickerWorker(TARGET_FPS);
        frameTicker.onmessage = () => {
          if (isDisposed) return;
          drawFrame();
          requestFrame?.();
        };
        frameTickerRef.current = frameTicker;
      } catch (error) {
        console.error("Failed to create frame ticker worker:", error);
      }
    };

    void start();

    return () => {
      isDisposed = true;
      if (frameTickerRef.current) {
        frameTickerRef.current.terminate();
        frameTickerRef.current = null;
      }
      screenVideo.pause();
      screenVideo.srcObject = null;
      cameraVideo.pause();
      cameraVideo.srcObject = null;
      stopOutput(outputStreamRef);
      setCompositedStream(null);
    };
  }, [cameraStream, screenStream]);

  // Manage audio tracks on the composited stream independently so mic toggles
  // during recording are reflected without tearing down the canvas pipeline.
  useEffect(() => {
    if (!compositedStream) return;
    compositedStream
      .getAudioTracks()
      .forEach(t => compositedStream.removeTrack(t));
    if (isMicEnabled && cameraStream) {
      const micTrack = cameraStream
        .getAudioTracks()
        .find(t => t.readyState === "live");
      if (micTrack) compositedStream.addTrack(micTrack);
    }
  }, [compositedStream, isMicEnabled, cameraStream]);

  return compositedStream;
};
